import { forwardRef, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AnimateHeight from 'react-animate-height';

import { getCountVideoCommentSelector, getVideoIdSelector } from '../selectors';
import { getCustomerSelector, withCommentsFeatureSelector } from 'common/selectors';
import {
  getVideoAttachmentsAvailableSelector,
  getVideoAttachmentsTotalNumberSelector,
} from '../video-attachments/selector';
import { VideoTab } from './fragments/video-tab';
import { VideoComments } from '../video-comments';
import { VideoAttachments } from '../video-attachments';
import { ButtonX } from '../../button/index';
import { translateForCustomer } from 'utils/i18n';
import { AlternativeAttachmentTab } from './fragments/alternative-attachment-tab';
import classNames from 'classnames';

const AttachmentTab = forwardRef(
  (
    {
      id,
      active,
      tabIndex,
      'data-e2e': dataE2e,
      'aria-label': ariaLabel,
      'aria-controls': ariaControls,
      onClick,
      onKeyDown,
    },
    ref
  ) => {
    const withAttachments = useSelector(getVideoAttachmentsAvailableSelector);
    const attachmentsTotal = useSelector(getVideoAttachmentsTotalNumberSelector);
    const customer = useSelector(getCustomerSelector) || {};

    return withAttachments ? (
      <VideoTab
        ref={ref}
        id={id}
        active={active}
        data-e2e={dataE2e}
        aria-label={ariaLabel}
        aria-expanded={active}
        aria-controls={ariaControls}
        tabIndex={tabIndex}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        {attachmentsTotal
          ? translateForCustomer('attachments.total', customer, { total: attachmentsTotal })
          : translateForCustomer('attachments', customer)}
      </VideoTab>
    ) : null;
  }
);

export const VideoTabsDecorated = () => {
  const { t } = useTranslation();
  const [showComments, setShowComments] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const customer = useSelector(getCustomerSelector);
  const videoId = useSelector(getVideoIdSelector);
  const withComments = useSelector(withCommentsFeatureSelector);
  const withAttachments = useSelector(getVideoAttachmentsAvailableSelector);
  const commentsTotal = useSelector(getCountVideoCommentSelector);
  const attachmentTabRef = useRef(null);
  const commentsTabRef = useRef(null);

  const toggleTabFocus = () => {
    commentsTabRef.current === document.activeElement
      ? attachmentTabRef.current.focus()
      : commentsTabRef.current.focus();
  };

  const handleKeyDown = (event) => {
    let flag = false;

    switch (event.key) {
      case 'Home':
      case 'End':
      case 'ArrowRight':
      case 'ArrowLeft':
        if (commentsTabRef.current && attachmentTabRef.current) {
          // We can toggle focus, because we have max 2 tabs
          toggleTabFocus();

          flag = true;
        }
        break;
      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (videoId) {
      collapseTabs();
    }
  }, [videoId]);

  const handleShowComments = () => {
    setShowComments(!showComments);
    setShowAttachments(false);
  };

  const handleShowAttachments = () => {
    setShowComments(false);
    setShowAttachments(!showAttachments);
  };

  const collapseTabs = () => {
    setShowComments(false);
    setShowAttachments(false);
  };

  const tabHeader = () => {
    if (showComments) {
      return 'comments';
    } else if (showAttachments) {
      return 'attachments';
    }
  };

  return (
    <div
      className={`ct-video-tabs${
        !showComments && !showAttachments ? '' : ' ct-video-tabs--margin'
      }${!withComments && !withAttachments ? ' ct-video-tabs--hidden' : ''}`}
    >
      <div role="tablist" className="ct-video-tabs__nav" aria-label={t('videoTabs')}>
        {withComments && (
          <VideoTab
            ref={commentsTabRef}
            id="video-comments-tab"
            data-e2e="video-comments-tab"
            aria-controls="video-comments-container"
            tabIndex={showAttachments ? -1 : undefined}
            active={showComments}
            onClick={handleShowComments}
            onKeyDown={handleKeyDown}
          >
            {t('comments')} {commentsTotal > 0 && `(${commentsTotal})`}
          </VideoTab>
        )}
        {customer && customer?.hasAlternativeAttachmentTab ? (
          <AlternativeAttachmentTab
            ref={attachmentTabRef}
            id="video-attachments-tab"
            data-e2e="alternative-video-attachments-tab"
            aria-controls="video-attachments-container"
            active={showAttachments}
            tabIndex={showComments ? -1 : undefined}
            onClick={handleShowAttachments}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <AttachmentTab
            ref={attachmentTabRef}
            id="video-attachments-tab"
            data-e2e="video-attachments-tab"
            aria-controls="video-attachments-container"
            active={showAttachments}
            tabIndex={showComments ? -1 : undefined}
            onClick={handleShowAttachments}
            onKeyDown={handleKeyDown}
          />
        )}
      </div>

      <div
        id={showAttachments ? 'video-attachments-container' : 'video-comments-container'}
        role="tabpanel"
        aria-labelledby={showAttachments ? 'video-attachments-tab' : 'video-comments-tab'}
        data-e2e="videoTabsContainer"
        className={classNames('ct-video-tabs__component-container', {
          'ct-video-tabs__component-container--hidden': !showComments && !showAttachments,
          'ct-video-tabs__component-container--no-border': customer && customer?.hasAlternativeAttachmentTab,
        })}
      >
        {tabHeader() && !(customer && customer?.hasAlternativeAttachmentTab) && (
          <div className="ct-video-tabs__component-container-nav">
            <h3 className="ct-video-tabs__header">{translateForCustomer(tabHeader(), customer)}</h3>
            <ButtonX
              aria-label={t('close')}
              disabled={showAttachments || showComments ? false : true}
              onClick={collapseTabs}
            />
          </div>
        )}
        <AnimateHeight height={showComments ? 'auto' : 0}>
          <VideoComments className={!showComments ? 'ct-video-tabs__hidden-component' : ''} />
        </AnimateHeight>
        <AnimateHeight height={showAttachments ? 'auto' : 0}>
          <VideoAttachments className={!showAttachments ? 'ct-video-tabs__hidden-component' : ''} />
        </AnimateHeight>
      </div>
    </div>
  );
};
